import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import DeleteIcon from '@material-ui/icons/Delete';
import { QuickLink, ValidationError } from './types';
import { SaveResult } from './useQuickLinks';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  fields: {
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
  },
  errorBanner: {
    marginBottom: theme.spacing(2),
    color: theme.palette.error.main,
  },
}));

function describeError(error: ValidationError): string {
  switch (error.type) {
    case 'too_many':
      return `Too many links: ${error.actual} (max ${error.max}).`;
    case 'empty_title':
      return `Link ${error.index + 1} is missing a title.`;
    case 'invalid_url':
      return `"${error.title}" has an invalid URL. Only http and https are allowed.`;
    default:
      return 'Validation failed.';
  }
}

function newLink(order: number): QuickLink {
  return {
    id: `link-${Date.now()}-${order}`,
    title: '',
    url: '',
    order,
  };
}

interface Props {
  open: boolean;
  links: QuickLink[];
  onSave: (links: QuickLink[]) => Promise<SaveResult>;
  onClose: () => void;
}

export const QuickLinksEditor = ({ open, links, onSave, onClose }: Props) => {
  const classes = useStyles();
  const [draft, setDraft] = useState<QuickLink[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ValidationError | null>(null);

  // Reset draft every time the dialog opens
  useEffect(() => {
    if (open) {
      setDraft(links.map((l, i) => ({ ...l, order: i })));
      setError(null);
    }
  }, [open, links]);

  const updateField = (
    index: number,
    field: 'title' | 'url',
    value: string,
  ) => {
    setDraft(prev =>
      prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)),
    );
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    setDraft(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((l, i) => ({ ...l, order: i }));
    });
  };

  const moveDown = (index: number) => {
    setDraft(prev => {
      if (index === prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((l, i) => ({ ...l, order: i }));
    });
  };

  const deleteLink = (index: number) => {
    setDraft(prev =>
      prev.filter((_, i) => i !== index).map((l, i) => ({ ...l, order: i })),
    );
  };

  const addLink = () => {
    setDraft(prev => [...prev, newLink(prev.length)]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const result = await onSave(draft);
    setSaving(false);
    if (!result.ok) {
      setError(result.error);
    }
    // onSave closes the dialog on success
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Customize Links</DialogTitle>
      <DialogContent>
        {error && (
          <Typography className={classes.errorBanner} variant="body2">
            {describeError(error)}
          </Typography>
        )}

        {draft.map((link, index) => (
          <Box key={link.id} className={classes.row}>
            <Box className={classes.fields}>
              <TextField
                label="Title"
                value={link.title}
                onChange={e => updateField(index, 'title', e.target.value)}
                variant="outlined"
                size="small"
                style={{ flex: 1 }}
                inputProps={{ 'aria-label': `Title for link ${index + 1}` }}
              />
              <TextField
                label="URL"
                value={link.url}
                onChange={e => updateField(index, 'url', e.target.value)}
                variant="outlined"
                size="small"
                style={{ flex: 2 }}
                inputProps={{ 'aria-label': `URL for link ${index + 1}` }}
              />
            </Box>
            <Box className={classes.controls}>
              <Tooltip title="Move up">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    aria-label={`Move link ${index + 1} up`}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => moveDown(index)}
                    disabled={index === draft.length - 1}
                    aria-label={`Move link ${index + 1} down`}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => deleteLink(index)}
                  aria-label={`Delete link ${index + 1}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addLink}
          size="small"
          variant="outlined"
        >
          Add Link
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
